import type { BuildSchematic } from '../types/buildTeam';
import type { SchematicDelivery } from '../types/buildSchematicDelivery';

/** A UI filter only: the backend and CDU agent validate the file contents. */
export function canDeliverSchematic(schematic: Pick<BuildSchematic, 'filename' | 'file_kind'>): boolean {
  return schematic.file_kind !== 'screenshot' && /\.(schem|schematic)$/i.test(schematic.filename);
}

export function deliveryIsActive(delivery: SchematicDelivery): boolean {
  return delivery.state === 'queued' || delivery.state === 'running';
}

/** Keep newly queued jobs and terminal states when an older poll finishes later. */
export function mergeSchematicDeliveries(current: SchematicDelivery[], incoming: SchematicDelivery[]): SchematicDelivery[] {
  const merged = new Map(current.map((delivery) => [delivery.id, delivery]));
  for (const delivery of incoming) {
    const previous = merged.get(delivery.id);
    const regresses = previous && ((!deliveryIsActive(previous) && deliveryIsActive(delivery)) || (previous.state === 'running' && delivery.state === 'queued'));
    if (!previous || (delivery.updated_at >= previous.updated_at && !regresses)) {
      merged.set(delivery.id, delivery);
    }
  }
  return [...merged.values()].sort((a, b) => b.created_at - a.created_at);
}

interface DeliveryRequest {
  requestId: string;
  deliveryId: string | null;
}

/** Retain uncertain requests across modal closes, navigation and reloads in this tab. */
export class SchematicDeliveryRequests {
  private readonly requests = new Map<string, DeliveryRequest>();

  constructor(private readonly storage?: Pick<Storage, 'getItem' | 'setItem'>) {}

  private key(schematicId: string, serverAddress: string): string {
    return `argus:schematic-delivery:${encodeURIComponent(schematicId)}:${encodeURIComponent(serverAddress)}`;
  }

  prepare(schematicId: string, serverAddress: string, deliveries: SchematicDelivery[]): string {
    const key = this.key(schematicId, serverAddress);
    let previous = this.requests.get(key);
    if (!previous) {
      try {
        const stored = JSON.parse(this.storage?.getItem(key) ?? 'null') as DeliveryRequest | null;
        if (stored && typeof stored.requestId === 'string' && /^[0-9a-f-]{36}$/i.test(stored.requestId) && (stored.deliveryId === null || typeof stored.deliveryId === 'string')) {
          previous = stored;
        }
      } catch { /* Storage can be unavailable in private browsing. */ }
    }
    const knownDelivery = previous?.deliveryId ? deliveries.find((delivery) => delivery.id === previous.deliveryId) : undefined;
    const request = previous && (!knownDelivery || deliveryIsActive(knownDelivery))
      ? previous
      : { requestId: crypto.randomUUID(), deliveryId: null };
    this.save(key, request);
    return request.requestId;
  }

  acknowledge(schematicId: string, serverAddress: string, requestId: string, deliveryId: string): void {
    this.save(this.key(schematicId, serverAddress), { requestId, deliveryId });
  }

  private save(key: string, request: DeliveryRequest): void {
    this.requests.set(key, request);
    try { this.storage?.setItem(key, JSON.stringify(request)); } catch { /* The in-memory identity still prevents duplicate retries. */ }
  }
}
